/**
 * @ais/plugin-host — runtime ViewerPlugin marketplace loader.
 *
 * Hosts (admin app, dashboard cells) construct a PluginHost over a Viewer
 * and use it to:
 *   1. listAvailable()  — fetch /plugins/registry.json
 *   2. install(entry)   — dynamic-import the plugin ES module, validate its
 *                         manifest engines vs the running viewer-core
 *                         version, then call viewer.use(plugin)
 *   3. uninstall(id)    — call viewer.dispose-of-plugin path (engine handles it)
 *
 * Installed plugins are persisted in localStorage so a reload re-installs
 * them before applySnapshot runs (so plugin state in extensions[] re-applies
 * cleanly). Persistence is per-app via a `storageKey` constructor option;
 * admin and dashboard pick different keys so they can have different
 * installed sets.
 *
 * Plugin bundles are expected to be ES modules whose default export is a
 * factory `() => ViewerPlugin`. Bundlers should mark `three`,
 * `@ais/viewer-core`, `@ais/viewer-ui` as external — the host's importmap
 * (set up out-of-band by the host app) redirects those bare specifiers to
 * the host's already-loaded copies, so plugins reuse the host's module
 * instances rather than dragging their own.
 */
import type { SnapshotPluginRef, Viewer, ViewerPlugin } from '@ais/viewer-core';
import {
  type PluginManifest,
  type PluginRegistry,
  type PluginRegistryEntry,
  satisfies,
  validateManifest,
} from './manifest';

export type {
  PluginKind,
  PluginType,
  PluginManifest,
  PluginRegistry,
  PluginRegistryEntry,
} from './manifest';
export { validateManifest, satisfies, PLUGIN_TYPES } from './manifest';

/** Persisted record of a successfully-installed plugin. */
export interface InstalledPluginRecord {
  id: string;
  version: string;
  manifestUrl: string;
}

export interface PluginHostOptions {
  /** Absolute URL of the registry index. Defaults to '/plugins/registry.json'. */
  registryUrl?: string;
  /** localStorage key for the installed-plugin list. Defaults to '@ais/plugin-host:installed'. */
  storageKey?: string;
  /** Engine version to check plugin manifest engines against. Defaults to '@ais/viewer-core' from package.json reflected at build time. */
  engineVersion: string;
  /**
   * Optional adapter-key probe. If a manifest declares contributes:['sidebar']
   * but the host hasn't mounted a sidebar adapter, install is refused. The
   * default checks `viewer.ui[key]` truthiness; tests can stub this.
   */
  hasAdapter?: (key: string) => boolean;
}

const DEFAULT_REGISTRY_URL = '/plugins/registry.json';
const DEFAULT_STORAGE_KEY = '@ais/plugin-host:installed';

export class PluginHost {
  private readonly viewer: Viewer;
  private registryUrl: string;
  private readonly storageKey: string;
  private readonly engineVersion: string;
  private readonly hasAdapter: (key: string) => boolean;
  private registryCache: PluginRegistry | null = null;
  private installed: Map<string, InstalledPluginRecord> = new Map();
  private listeners: Set<() => void> = new Set();

  constructor(viewer: Viewer, opts: PluginHostOptions) {
    this.viewer = viewer;
    this.registryUrl = opts.registryUrl ?? DEFAULT_REGISTRY_URL;
    this.storageKey = opts.storageKey ?? DEFAULT_STORAGE_KEY;
    this.engineVersion = opts.engineVersion;
    this.hasAdapter =
      opts.hasAdapter ??
      ((key: string) => {
        const ui = (viewer as unknown as { ui: Record<string, unknown> }).ui;
        return !!ui && !!ui[key];
      });
    this.loadInstalledFromStorage();
  }

  // -------------------------------------------------------------------
  // Registry
  // -------------------------------------------------------------------

  /** The registry endpoint this host currently reads (default '/plugins/registry.json'). */
  getRegistryUrl(): string {
    return this.registryUrl;
  }

  /**
   * Re-point this host at a different registry (e.g. the trusted-zone
   * marketplace on another origin). Clears the cached registry list so the
   * next listAvailable() refetches. Installed plugins are untouched.
   */
  setRegistryUrl(url: string): void {
    const next = (url || '').trim() || DEFAULT_REGISTRY_URL;
    if (next === this.registryUrl) return;
    this.registryUrl = next;
    this.registryCache = null;
    this.emitChange();
  }

  /**
   * Resolve a registry-relative URL (entry.manifestUrl is usually the
   * origin-absolute '/plugins/<id>/<v>/manifest.json' the marketplace wrote)
   * against the CURRENT registry endpoint, so a registry hosted on another
   * origin brings its manifests + bundles with it. For the same-origin
   * default this is byte-identical to the old page-origin resolution.
   */
  private resolveFromRegistry(url: string): string {
    return new URL(url, new URL(this.registryUrl, window.location.href)).toString();
  }

  /** Fetch the marketplace registry. Caches the result per-instance. */
  async listAvailable(force = false): Promise<PluginRegistryEntry[]> {
    if (!this.registryCache || force) {
      const r = await fetch(this.registryUrl, { cache: force ? 'reload' : 'default' });
      if (!r.ok) throw new Error(`registry fetch ${this.registryUrl} → HTTP ${r.status}`);
      const json = (await r.json()) as PluginRegistry;
      if (!json || !Array.isArray(json.plugins)) {
        throw new Error('registry.json malformed: expected { plugins: [...] }');
      }
      this.registryCache = json;
    }
    return this.registryCache.plugins;
  }

  /** Plugins installed in this PluginHost instance, in insertion order. */
  listInstalled(): InstalledPluginRecord[] {
    return [...this.installed.values()];
  }

  /**
   * Subscribe to install-set changes. Fires after install / uninstall / each
   * restoreInstalled iteration so UI surfaces (admin's PluginBrowser panel)
   * can repaint without polling. Returns an unsubscribe fn.
   */
  onChange(cb: () => void): () => void {
    this.listeners.add(cb);
    return () => { this.listeners.delete(cb); };
  }

  private emitChange(): void {
    for (const cb of this.listeners) {
      try { cb(); } catch (e) { console.error('[plugin-host] onChange listener threw', e); }
    }
  }

  // -------------------------------------------------------------------
  // Install / uninstall
  // -------------------------------------------------------------------

  /**
   * Install a plugin from a registry entry. Fetches the manifest, validates
   * engine compatibility, dynamic-imports the bundle, calls viewer.use(),
   * and persists the install. Idempotent: re-installing an already-installed
   * id is a no-op (returns the existing record).
   */
  async install(entry: PluginRegistryEntry): Promise<InstalledPluginRecord> {
    // Idempotent against the *viewer*, not just our installed-record map.
    // The map is rehydrated from localStorage at construction, so a fresh
    // PluginHost over a fresh Viewer can have records for plugins the
    // viewer hasn't actually been given yet (dashboard creates a new
    // Viewer per snapshot drop). Only short-circuit when the viewer truly
    // already has this plugin instance.
    if (this.installed.has(entry.id) && this.viewer.getPlugin(entry.id)) {
      return this.installed.get(entry.id)!;
    }
    return this.installManifest(await this.fetchManifest(entry.manifestUrl), entry.manifestUrl);
  }

  /** The install proper, for a manifest already fetched from `manifestUrl`. */
  private async installManifest(manifest: PluginManifest, manifestUrl: string): Promise<InstalledPluginRecord> {
    this.assertCompatible(manifest);
    this.warnUnmetContributes(manifest);

    const moduleUrl = absoluteUrl(manifest.entryUrl, this.resolveFromRegistry(manifestUrl));
    // Vite warns about non-literal dynamic imports; suppressed because the
    // URL is data-driven by the registry, not bundled at build time.
    const mod = (await import(/* @vite-ignore */ moduleUrl)) as {
      default?: () => ViewerPlugin;
    };
    if (typeof mod.default !== 'function') {
      throw new Error(
        `plugin '${manifest.id}' bundle has no default export factory (expected '() => ViewerPlugin')`,
      );
    }
    const plugin = mod.default();
    if (plugin.id !== manifest.id) {
      throw new Error(
        `plugin id mismatch: manifest says '${manifest.id}', factory returned '${plugin.id}'`,
      );
    }
    await this.viewer.use(plugin);
    const record: InstalledPluginRecord = {
      id: manifest.id,
      version: manifest.version,
      manifestUrl,
    };
    this.installed.set(record.id, record);
    this.saveInstalledToStorage();
    this.emitChange();
    return record;
  }

  /**
   * Uninstall a plugin by id. Calls viewer.dispose-style cleanup via the
   * plugin's own uninstall hook (the engine forwards through Viewer.dispose
   * or a future explicit removePlugin API; for now we call uninstall directly
   * and remove from viewer.plugins).
   */
  uninstall(id: string): void {
    const record = this.installed.get(id);
    if (!record) return;
    const plugin = this.viewer.getPlugin(id);
    if (plugin) {
      try { plugin.uninstall?.(this.viewer); } catch (e) { console.error(`plugin '${id}' uninstall threw`, e); }
      this.viewer.plugins.delete(id);
    }
    this.installed.delete(id);
    this.saveInstalledToStorage();
    this.emitChange();
  }

  /**
   * Re-install every plugin recorded in localStorage. Call once on app boot
   * before any applySnapshot — otherwise snapshots referencing plugin
   * extensions would log "skipped" warnings even though the user has the
   * plugin installed. Failures are logged and skipped, not thrown, so one
   * broken plugin doesn't block the rest.
   *
   * Prefers the registry's current entry for each id over the recorded
   * manifestUrl, so plugins auto-upgrade to the version the marketplace
   * is shipping today. Falls back to the recorded manifestUrl when the
   * id is no longer in the registry (so locally-installed plugins from
   * a private registry still restore after a refresh).
   */
  async restoreInstalled(): Promise<void> {
    const records = [...this.installed.values()];
    // Clear the in-memory map; install() re-adds successful entries.
    this.installed.clear();
    const available = await this.listAvailable().catch(() => [] as PluginRegistryEntry[]);
    for (const r of records) {
      try {
        const current = available.find((e) => e.id === r.id);
        const manifestUrl = current?.manifestUrl ?? r.manifestUrl;
        const manifest = await this.fetchManifest(manifestUrl);
        await this.install({
          id: manifest.id,
          version: manifest.version,
          label: manifest.label,
          description: manifest.description,
          author: manifest.author,
          manifestUrl,
        });
      } catch (e) {
        console.warn(`[plugin-host] failed to restore '${r.id}': ${(e as Error).message}`);
      }
    }
  }

  /**
   * Auto-install plugins a snapshot relies on that aren't already installed.
   *
   * Two inputs, so both older and newer snapshots resolve correctly:
   *   - `extensions` — the per-plugin STATE block (keys are plugin ids). Every
   *     id here needs its plugin present so its `applyState` runs.
   *   - `marks` — the optional SERIALIZATION MARK (`snapshot.plugins`). Each
   *     mark carries the `manifestUrl` the plugin resolved from at save time.
   *
   * Resolution per id: when a mark carries a `manifestUrl`, install from THAT
   * url first (the exact-version pin the author saved with). On any failure —
   * 404, unreachable store, engine mismatch — or when there is no mark, FALL
   * BACK to today's behaviour: look the id up in the current registry and
   * install the marketplace's shipping version. This is the hard fallback
   * contract: an absent mark leaves resolution byte-identical to before, and a
   * stale/unreachable mark degrades to the id-lookup rather than failing.
   *
   * Returns the ids that couldn't be resolved either way, so the host UI can
   * render an "unknown plugin" hint instead of failing silently. Return shape
   * unchanged from the single-argument form.
   */
  async installForSnapshot(
    extensions: Record<string, unknown>,
    marks?: ReadonlyArray<SnapshotPluginRef>,
  ): Promise<{
    installed: string[];
    missing: string[];
  }> {
    const markById = new Map(
      (marks ?? [])
        .filter((m): m is SnapshotPluginRef => !!m && typeof m.id === 'string')
        .map((m) => [m.id, m]),
    );
    // Union of state-bearing ids and marked ids; preserves extensions order so
    // the no-mark path iterates exactly as the old `Object.keys(extensions)` did.
    const ids = new Set<string>([...Object.keys(extensions), ...markById.keys()]);
    const installed: string[] = [];
    const missing: string[] = [];
    if (ids.size === 0) return { installed, missing };

    const available = await this.listAvailable().catch(() => [] as PluginRegistryEntry[]);
    for (const id of ids) {
      // Same caveat as install(): rehydrated localStorage entries don't
      // imply the current viewer has the plugin. Re-resolve via registry
      // and re-install when the viewer is missing it.
      if (this.installed.has(id) && this.viewer.getPlugin(id)) continue;

      // Prefer the recorded manifestUrl (exact version pin) when the snapshot
      // marks this plugin. On failure, fall through to the registry id-lookup.
      const mark = markById.get(id);
      if (mark?.manifestUrl) {
        try {
          await this.installFromManifestUrl(mark.manifestUrl);
          installed.push(id);
          continue;
        } catch (e) {
          console.warn(
            `[plugin-host] mark install of '${id}' via ${mark.manifestUrl} failed, ` +
              `falling back to registry: ${(e as Error).message}`,
          );
        }
      }

      const entry = available.find((e) => e.id === id);
      if (!entry) {
        missing.push(id);
        continue;
      }
      // Dashboard widgets share the registry with viewer plugins but don't
      // implement ViewerPlugin; trying to install one into a Viewer would
      // throw. Treat them as missing for this host.
      if ((entry.kind ?? 'viewer-plugin') !== 'viewer-plugin') {
        missing.push(id);
        continue;
      }
      try {
        await this.install(entry);
        installed.push(id);
      } catch (e) {
        console.warn(`[plugin-host] auto-install of '${id}' failed: ${(e as Error).message}`);
        missing.push(id);
      }
    }
    return { installed, missing };
  }

  /**
   * Open a scene AS AN APP — install the app named by the scene's `app` mark
   * and the plugins its manifest `dependsOn`, and NOTHING ELSE the scene
   * marks. The mark lists whatever the last editor to save the file had
   * installed (on a hub, the full editor pins every plugin it mirrors); an
   * app is a workspace, not an addition to a pile, and installing that list
   * here turned a Model Comparison scene into the full editor — 17 plugins
   * and every section, in both the dashboard's BIM Overlay cell and the
   * app's own door (live 2026-09-03). The scene's `extensions` for the other
   * plugins stay untouched in the JSON (§6) and simply aren't applied.
   *
   * Resolution is the mark's: the app from its recorded `manifestUrl` first,
   * the registry by id when that is absent or fails; each dependency through
   * {@link installForSnapshot} with its own mark (pinned version) when the
   * scene has one, the registry otherwise. Returns the app's manifest so the
   * host can put up its declared `ui` — the same block the store derives
   * `app-cfg.json` from, one vocabulary. Throws when the app itself cannot
   * be resolved; a host then falls back to loading the scene plainly.
   */
  async installApp(
    app: SnapshotPluginRef,
    marks?: ReadonlyArray<SnapshotPluginRef>,
  ): Promise<{ manifest: PluginManifest; installed: string[]; missing: string[] }> {
    let manifest: PluginManifest | undefined;
    let manifestUrl = app.manifestUrl;
    if (manifestUrl) {
      try {
        manifest = await this.fetchManifest(manifestUrl);
      } catch (e) {
        console.warn(
          `[plugin-host] app '${app.id}' via ${manifestUrl} failed, falling back to registry: ${(e as Error).message}`,
        );
      }
    }
    if (!manifest) {
      const entry = (await this.listAvailable()).find(
        (e) => e.id === app.id && (e.kind ?? 'viewer-plugin') === 'viewer-plugin',
      );
      if (!entry) throw new Error(`app '${app.id}' is not in this host's registry`);
      manifestUrl = entry.manifestUrl;
      manifest = await this.fetchManifest(manifestUrl);
    }
    if (manifest.id !== app.id) {
      throw new Error(`app mark says '${app.id}', its manifest says '${manifest.id}'`);
    }
    if (!(this.installed.has(manifest.id) && this.viewer.getPlugin(manifest.id))) {
      await this.installManifest(manifest, manifestUrl!);
    }
    const markById = new Map((marks ?? []).filter((m) => !!m && typeof m.id === 'string').map((m) => [m.id, m]));
    const deps = (manifest.dependsOn ?? []).filter((id) => id !== manifest!.id);
    const r = await this.installForSnapshot({}, deps.map((id) => markById.get(id) ?? { id }));
    return { manifest, installed: [manifest.id, ...r.installed], missing: r.missing };
  }

  /**
   * Install straight from a manifest URL — the serialization-mark path. Fetches
   * + validates the manifest at that url (so the recorded exact version is what
   * loads), refuses non-viewer-plugin kinds (a Viewer host can't run them), then
   * delegates to {@link install}. Mirrors {@link restoreInstalled}'s "fetch the
   * manifest, install from its fields + this url" shape. Throws on any failure
   * so the caller can fall back to a registry id-lookup.
   *
   * PUBLIC because a host may install an exact version it was handed rather than
   * one it looked up: the admin's `?plugin=<manifestUrl>` review deep-link, which
   * the store's "Open in editor" uses so a reviewer meets the plugin loaded, not
   * merely its cfg applied. A caller taking that url from anywhere but its own
   * store is executing remote code by link — validate the origin first, as the
   * admin does.
   */
  async installFromManifestUrl(manifestUrl: string): Promise<InstalledPluginRecord> {
    const manifest = await this.fetchManifest(manifestUrl);
    if ((manifest.kind ?? 'viewer-plugin') !== 'viewer-plugin') {
      throw new Error(
        `plugin '${manifest.id}' is kind '${manifest.kind}', not a viewer-plugin`,
      );
    }
    return this.install({
      id: manifest.id,
      version: manifest.version,
      label: manifest.label,
      description: manifest.description,
      author: manifest.author,
      manifestUrl,
    });
  }

  // -------------------------------------------------------------------
  // Internals
  // -------------------------------------------------------------------

  private async fetchManifest(url: string): Promise<PluginManifest> {
    const r = await fetch(this.resolveFromRegistry(url));
    if (!r.ok) throw new Error(`manifest fetch ${url} → HTTP ${r.status}`);
    return validateManifest(await r.json());
  }

  private assertCompatible(manifest: PluginManifest): void {
    const range = manifest.engines['@ais/viewer-core'];
    if (range && !satisfies(this.engineVersion, range)) {
      throw new Error(
        `plugin '${manifest.id}@${manifest.version}' requires viewer-core ${range}, host has ${this.engineVersion}`,
      );
    }
  }

  /**
   * `contributes` lists UI adapters the plugin would *like* to attach to
   * (e.g. 'sidebar'). It is informational, not gating: a plugin's own
   * install() decides whether it can run headless when an adapter is
   * absent. Refusing install here would block dashboards (which mount no
   * Sidebar) from receiving plugin applyState, dropping snapshot extension
   * effects like MIC's BIM-translucent preset. We log instead.
   */
  private warnUnmetContributes(manifest: PluginManifest): void {
    const wanted = manifest.contributes ?? [];
    const missing = wanted.filter((k) => !this.hasAdapter(k));
    if (missing.length > 0) {
      console.info(
        `[plugin-host] '${manifest.id}' contributes ${missing.join(', ')} but host has no such adapter — plugin will run with reduced UI.`,
      );
    }
  }

  private loadInstalledFromStorage(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      const raw = localStorage.getItem(this.storageKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as InstalledPluginRecord[];
      if (!Array.isArray(parsed)) return;
      for (const r of parsed) {
        if (r && typeof r.id === 'string' && typeof r.manifestUrl === 'string') {
          this.installed.set(r.id, r);
        }
      }
    } catch (e) {
      console.warn(`[plugin-host] failed to read ${this.storageKey}: ${(e as Error).message}`);
    }
  }

  private saveInstalledToStorage(): void {
    if (typeof localStorage === 'undefined') return;
    try {
      localStorage.setItem(this.storageKey, JSON.stringify([...this.installed.values()]));
    } catch (e) {
      console.warn(`[plugin-host] failed to write ${this.storageKey}: ${(e as Error).message}`);
    }
  }
}

/** Resolve an entryUrl that may be relative-to-manifest into an absolute URL. */
function absoluteUrl(entryUrl: string, manifestUrl: string): string {
  if (/^(https?:)?\/\//.test(entryUrl) || entryUrl.startsWith('/')) return entryUrl;
  return new URL(entryUrl, new URL(manifestUrl, window.location.href)).toString();
}
